import axios from "axios";
import type { DeclineContext, DeclineGiftBody } from "@/interfaces/decline";
import { apiClient } from "@/lib/apiClient";

/**
 * Recipient "decline my gift" endpoints (Hono `declineRoutes`).
 *
 * Auth is the signed token from the reminder e-mail, not Clerk — the same arrangement as
 * `api/redemption.ts`. The token travels in the path; the page reads it from `?token=` because
 * that is the URL shape already sitting in recipients' inboxes (`getDeclineGiftUrl`).
 */

export async function fetchDeclineContext(token: string): Promise<DeclineContext> {
  const { data } = await apiClient.get<{ context: DeclineContext }>(`/decline/${token}`);
  return data.context;
}

/**
 * Records the decline. Resolves on "already declined" too — the API answers 200 for it, since
 * landing here twice is a double-submit, not a failure.
 *
 * Throws with the server's own sentence on a 409 (the gift ended some other way), so the page can
 * tell the recipient something true instead of a generic error.
 */
export async function submitDecline(token: string, body: DeclineGiftBody): Promise<void> {
  try {
    await apiClient.post(`/decline/${token}`, body);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("We couldn't record that just now. Please try again.");
  }
}
